import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, PlayCircle, ArrowRight } from "lucide-react";
import { api, img } from "../lib/api";
import { useApp } from "../context/AppContext";
import { HeroOrbit } from "../components/HeroOrbit";
import { FeatureRail, CollectionCard, TrustRail } from "../components/HomeSections";
import { ProductGrid } from "../components/ProductCard";

import { useCategories } from "../components/CategoryMenu";
import { tx, useLang } from "../lib/i18n";
const SectionHead = ({ title, to, label, testid }) => { useLang(); return (
  <div className="mb-5 flex items-end justify-between">
    <h2 className="text-lg md:text-xl font-medium text-[var(--mv-text)]">{title}</h2>
    <Link to={to} data-testid={testid} className="group flex items-center gap-2 text-xs text-[var(--mv-muted)] hover:text-[var(--mv-teal)]">{tx(label)} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></Link>
  </div>
); };

const FeaturedCategories = () => {
  useLang();
  const tree = useCategories().filter((c) => c.featured);
  if (!tree.length) return null;
  return (
    <section className="mt-16" data-testid="featured-categories">
      <h2 className="mb-5 text-2xl font-semibold">{tx("Shop by category")}</h2>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {tree.map((c) => (
          <Link key={c.slug} to={`/shop?category=${c.slug}`} className="group rounded-2xl border border-[var(--mv-border)] bg-[var(--mv-card)] p-4 transition-colors hover:border-[var(--mv-teal)]" data-testid={`featured-category-${c.slug}`}>
            {c.image ? <img src={img(c.image)} alt="" className="mb-3 aspect-square w-full rounded-xl object-cover" /> : <div className="mb-3 grid aspect-square place-items-center rounded-xl bg-[var(--mv-teal-dim)] text-2xl font-semibold text-[var(--mv-teal)]">{(c.display_name || c.name).slice(0, 1)}</div>}
            <p className="text-sm font-semibold group-hover:text-[var(--mv-teal)]">{c.display_name || c.name}</p>
          </Link>
        ))}
      </div>
    </section>
  );
};

const FeaturedStores = () => { const lang = useLang();
  const [stores, setStores] = useState([]);
  useEffect(() => { api.get("/featured-stores").then((r) => setStores(r.data)).catch(() => {}); }, [lang]);
  if (!stores.length) return null;
  return (
    <section data-testid="featured-stores-section">
      <SectionHead title={tx("Featured stores")} to="/stores" label={tx("All stores")} testid="view-all-stores-link" />
      <div className="grid gap-4 md:grid-cols-3">{stores.map((s) => (
        <Link key={s.slug} to={`/stores/${s.slug}`} className="flex items-center gap-4 rounded-2xl border border-[var(--mv-border)] bg-[var(--mv-card)] p-5 transition-colors hover:border-[var(--mv-teal)]" data-testid={`featured-store-${s.slug}`}>
          {tx(s.logo ? <img src={img(s.logo)} alt="" className="h-12 w-12 rounded-xl object-cover" /> : <span className="grid h-12 w-12 place-items-center rounded-xl bg-[var(--mv-teal-dim)] text-lg font-semibold text-[var(--mv-teal)]">{tx(s.name[0])}</span>)}
          <div><p className="font-semibold">{tx(s.name)}</p><p className="text-xs text-[var(--mv-muted)] line-clamp-1">{tx(s.description)}</p></div>
        </Link>))}</div>
    </section>
  );
};

const HeroText = ({ s }) => { useLang();
  const { t } = useApp();
  const [l1, ...rest] = tx(s.headline || "Shop in motion.").split(" ");
  return (
    <div className="relative z-20 mv-reveal">
      <p className="text-[11px] sm:text-xs font-semibold tracking-[0.16em] text-[var(--mv-teal)]" data-testid="hero-eyebrow">{tx(s.eyebrow)}</p>
      <h1 className="mt-5 text-5xl sm:text-6xl lg:text-[76px] font-semibold leading-[0.98] tracking-[-0.03em] text-[var(--mv-text)]" data-testid="hero-headline">
        {tx(l1)} {rest.slice(0, 1).join(" ")}<br />{rest.slice(1).join(" ")}
      </h1>
      <p className="mt-6 max-w-[360px] text-[15px] leading-relaxed text-[var(--mv-text)]/85" data-testid="hero-description">{tx(s.description)}</p>
      <div className="mt-8 flex flex-wrap gap-3">
        <Link to={s.primary_cta_link || "/shop"} data-testid="hero-shop-collection-button" className="mv-btn-primary flex items-center gap-2.5 rounded-lg px-6 py-3.5 text-sm">
          {tx(s.primary_cta_label || t("hero.shop"))} <ArrowUpRight className="h-4 w-4" />
        </Link>
        <Link to={s.secondary_cta_link || "/about"} data-testid="hero-explore-button" className="mv-btn-outline flex items-center gap-2.5 rounded-lg px-6 py-3.5 text-sm">
          {tx(s.secondary_cta_label || t("hero.explore"))} <PlayCircle className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
};

const LifestyleCard = ({ s }) => { useLang(); return (
  <Link to={s.lifestyle_link || "/collections"} data-testid="hero-lifestyle-card"
    className="absolute bottom-2 right-0 z-20 hidden md:flex w-[270px] items-center gap-3 overflow-hidden rounded-xl mv-glass p-3 pr-0 hover:border-[var(--mv-border-2)]">
    <div className="pl-1">
      <p className="text-xs font-semibold text-amber-300">{tx(s.lifestyle_title)}</p>
      <p className="mt-1 text-[11px] leading-snug text-[var(--mv-muted)]">{tx(s.lifestyle_subtitle)}</p>
    </div>
    <img src={img(s.lifestyle_image)} alt="" className="ml-auto h-[74px] w-[120px] rounded-l-lg object-cover" />
  </Link>
); };

export default function Home() { const lang = useLang();
  const { t } = useApp();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/home").then((r) => setData(r.data)); }, [lang]);
  const s = data?.settings || {};

  return (
    <div data-testid="home-page">
      <section className="relative overflow-hidden">
        <div className="mv-container grid items-center gap-6 pt-10 pb-6 lg:grid-cols-12 lg:pt-12 lg:pb-8 min-h-[560px]">
          <div className="lg:col-span-4 xl:col-span-4 lg:pl-4">{tx(data ? <HeroText s={s} /> : <div className="h-72" />)}</div>
          <div className="relative lg:col-span-8 xl:col-span-8 -mx-4 lg:mx-0 lg:-mr-6">
            <HeroOrbit objects={data?.hero_objects || []} enabled={s.hero_motion_enabled !== false} />
            {tx(data && <LifestyleCard s={s} />)}
          </div>
        </div>
      </section>

      <div className="mv-container space-y-14">
        <FeatureRail />
        <section data-testid="collections-section">
          <SectionHead title={t("home.collections")} to="/collections" label={t("home.viewCollections")} testid="view-all-collections-link" />
          <div className="grid gap-5 md:grid-cols-3">
            {(data?.collections || []).slice(0, 3).map((c, i) => <CollectionCard key={c.slug} c={c} idx={i} />)}
          </div>
        </section>
        <section data-testid="new-arrivals-section">
          <SectionHead title={t("home.arrivals")} to="/shop?sort=newest" label={t("home.viewAll")} testid="view-all-arrivals-link" />
          <ProductGrid items={data?.new_arrivals || []} cols="grid-cols-2 md:grid-cols-3 xl:grid-cols-6" testid="new-arrivals-grid" />
        </section>
        <FeaturedCategories />
        <FeaturedStores />
        <TrustRail items={s.trust_items || []} />
      </div>
    </div>
  );
}
